import { Card, CardContent, CardHeader } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { HeatMapChart } from "../../../components/HeatMapChart/HeatMapChart";
import { HeatMapOption } from "../../../components/HeatMapChart/HeatMapChart.types";
import { useEnigmaTotalMatchesCount } from "../../../hooks/enigmaStats.hooks";
import { MatchFilter } from "../../../libs/statsFilters";
import { WorkerQueryTransferModes } from "../../../libs/workers/consts";
import { webWorkerQuery } from "../../../libs/workers/webWorker";
import { useChallengeMatchOverviewDialog } from "../../../providers/ChallengeMatchOverviewDialogProvider";
import {
  EnigmaHeatMapEntry,
  RawHeatMapRow,
} from "./EnigmaTimelinesHeatMapChart.types";
import EnigmaTimelinesHeatMapChartWorker from "./EnigmaTimelinesHeatMapChart.worker?worker";

export const DEFAULT_HEATMAP_OPTIONS: HeatMapOption[] = [
  { label: "1min", interval: [0, 60], timeslot: 0 },
  { label: "2min", interval: [60, 120], timeslot: 1 },
  { label: "3min", interval: [120, 180], timeslot: 2 },
  { label: "4min", interval: [180, 240], timeslot: 3 },
  { label: "5min", interval: [240, 300], timeslot: 4 },
  { label: "5-10min", interval: [300, 600], timeslot: 5 },
  { label: "10-30min", interval: [600, 1800], timeslot: 6 },
  { label: "30min-1h", interval: [1800, 3600], timeslot: 7 },
  { label: "1-3h", interval: [3600, 10800], timeslot: 8 },
  { label: "3h+", interval: [10800, Infinity], timeslot: 9 },
];

Object.freeze(DEFAULT_HEATMAP_OPTIONS);

export interface EnigmaTimelinesHeatMapProps {
  cityEscapeId?: string | null;
  enigmaId?: string;
  loading?: boolean;
  totalHints?: number;
  elevation?: number;
  filters?: MatchFilter;
}

// Helper function to process data with worker - send raw TimelineEntry[]
async function processEnigmaDataWithWorker(
  {
    cityEscapeId,
    enigmaId,
    filters,
  }: { cityEscapeId?: string | null; enigmaId?: string; filters?: MatchFilter },
  totalHints: number,
  options: HeatMapOption[],
  selectedFilterKeys?: string[] | null
): Promise<RawHeatMapRow[]> {
  console.log("Starting EnigmaTimelinesHeatMapChartWorker with data:", {
    cityEscapeId,
    enigmaId,
    totalHints,
  });
  const worker = new EnigmaTimelinesHeatMapChartWorker();
  // ✅ Usa utility standardizzata per SharedArrayBuffer
  const result = await webWorkerQuery(worker, {
    type: "PROCESS_ENIGMA_DATA",
    data: {
      cityEscapeId,
      enigmaId,
      filters,
      totalHints,
      options,
      selectedFilterKeys,
    },
    opts: {
      traceKey: `EnigmaTimelinesHeatMapChartWorker-${Date.now()}`,
      response: { transferMode: WorkerQueryTransferModes.SHARED_ARRAY_BUFFER },
    },
  })
    .then((res) => {
      // console.log("EnigmaTimelinesHeatMapChartWorker result:", res);
      return res;
    })
    .catch((err) =>
      console.error(
        "Error processing Enigma data in EnigmaTimelinesHeatMapChartWorker:",
        err
      )
    )
    .finally(() => {
      worker.terminate();
    });

  // console.log("EnigmaTimelinesHeatMapChartWorker result:", result);

  return result;
}

function useEnigmaData(
  cityEscapeId?: string | null,
  enigmaId?: string,
  filters?: MatchFilter,
  totalHints: number = 5,
  options: HeatMapOption[] = DEFAULT_HEATMAP_OPTIONS
) {
  return useQuery({
    queryKey: [
      "EnigmaTimelinesHeatMapChartData",
      cityEscapeId,
      enigmaId,
      totalHints,
      JSON.stringify(filters),
      JSON.stringify(options),
    ],
    queryFn: () =>
      processEnigmaDataWithWorker(
        { cityEscapeId, enigmaId, filters },
        totalHints,
        options
      ),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: cityEscapeId !== undefined,
  });
}

export function EnigmaTimelinesHeatMap({
  cityEscapeId,
  enigmaId,
  filters,
  totalHints = 5,
  elevation = 0,
  loading = false,
}: EnigmaTimelinesHeatMapProps) {
  const data = useEnigmaData(
    cityEscapeId,
    enigmaId,
    filters,
    totalHints,
    DEFAULT_HEATMAP_OPTIONS
  );

  const totalMatches =
    useEnigmaTotalMatchesCount(cityEscapeId, enigmaId).data ?? 0;
  const dialog = useChallengeMatchOverviewDialog("EnigmaTimelinesHeatMapChart");

  const handleViewDetails = (entries: EnigmaHeatMapEntry[]) => {
    // console.log("View details:", entries);

    const data = [];
    for (const entry of entries) {
      const [matchId, targetId] = entry.filterKey.split("@");
      data.push({ matchId, targetId });
    }

    dialog.openModal({ title: "Dettagli Enigma", loading: false, data });
    // dialog.setData(entries.flatMap((e) => e.matches));
  };

  return (
    <Card elevation={elevation} sx={{ width: "100%", height: "100%" }}>
      <CardHeader title="Enigma Timelines Heatmap" />
      <CardContent sx={{ height: "calc(100% - 70px)" }}>
        <HeatMapChart
          rows={data.data ?? []}
          options={DEFAULT_HEATMAP_OPTIONS}
          totalItems={totalMatches}
          loading={data.isLoading}
          onViewDetails={(entries: EnigmaHeatMapEntry[]) =>
            handleViewDetails(entries)
          }
          cacheKey={`${cityEscapeId}-${enigmaId}`}
        />
      </CardContent>
    </Card>
  );
}
